"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { CAMPAIGN_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCampaigns from "@/hooks/requests/campaigns/use-get-campaigns";
import { useButtonPermission } from "@/hooks/use-button-permission";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { ICampaign } from "@/interfaces/campaign.interface";
import { CampaignsMessages } from "@/messages/campaigns/campaigns.messages";

import CampaignFiltersForm from "./components/CampaignFiltersForm";
import { CampaignTable } from "./components/CampaignTable";
import { initialFilterValues } from "./constants";

type Props = {
  messages: CampaignsMessages;
};

const FIRST_PAGE = 0;
const CARD_MARGIN_TOP = 16;
const LOADING_SPINNER_SIZE = 50;

function CampaignsPage({ messages }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters } = useFilters<ICampaign>({
    initialValue: initialFilterValues,
  });
  const campaignPermission = useButtonPermission(CAMPAIGN_PERMISSIONS.CREATE);

  const { data, isLoading, error } = useGetCampaigns({ filters, pageNumber, pageSize });

  const shouldLoadCampaignsTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoCampaignsFound = !!data && !isLoading && !error && data.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(filters) => {
            changePage(FIRST_PAGE);
            changeFilters(filters);
          }}
        >
          <CampaignFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.pageTitle}</MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createCampaign(lang))}
                  disabled={campaignPermission.disabled}
                  isLoading={campaignPermission.loading}
                  endIcon={<AddCircleOutlineIcon />}
                >
                  {messages.addCampaignButton}
                </MDButton>
              </MDBox>

              {shouldLoadCampaignsTable && (
                <CampaignTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.campaignErrorToLoad}
                  description={error.message}
                  alt={messages.campaignNotFoundAlt}
                />
              )}

              {shouldLoadNoCampaignsFound && (
                <AlertBlock
                  title={messages.campaignNotFoundError}
                  description={messages.campaignNotFoundDescription}
                  alt={messages.campaignNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default CampaignsPage;
